import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Descriptions,
  Popconfirm,
  App,
  Collapse,
  Spin,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PrinterOutlined,
  CheckOutlined,
  StopOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { purchaseReturnService } from '@/services/purchaseReturn.service';
import { supplierDebitNoteService as dnService } from '@/services/supplierDebitNote.service';
import type { PurchaseReturnDTO, PurchaseReturnLineDTO } from '@/services/purchaseReturn.service';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_PURCHASE_RETURN } from '@/constants/approval-entity-types';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { PrintablePurchaseReturn } from '@/components/procurement/PrintablePurchaseReturn';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_RETURN } from '@/constants/document-entity-types';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { formatMoney, formatNumber, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

const { Title, Text } = Typography;

/** Operator guidance for Purchase Return post / approve refusals, keyed by the API error code. */
const RETURN_ACTION_GUIDANCE: Record<string, { title: string; guidance: string }> = {
  LEGACY_RECEIPT_VALUATION_UNKNOWN: {
    title: 'This receipt cannot be returned through the system',
    guidance:
      'The GRN line was received before receipt valuation was recorded, so its original value is not known and cannot be reversed automatically. Contact Finance before returning these goods.',
  },
  RECEIPT_VALUATION_INCONSISTENT: {
    title: 'The GRN receipt valuation is inconsistent',
    guidance: 'The recorded receipt value of the GRN line does not match its accepted quantity. Contact Finance; the return cannot be posted until it is reviewed.',
  },
  LANDED_COST_RETURN_VARIANCE_ACCOUNT_NOT_CONFIGURED: {
    title: 'Finance setup required',
    guidance:
      'These goods carried landed cost (freight / overhead). A Finance administrator must select the Landed Cost Return Variance account under Company Profile → Sales & inventory GL defaults before this return can be posted.',
  },
  LANDED_COST_RETURN_VARIANCE_ACCOUNT_INVALID: {
    title: 'Finance setup required',
    guidance:
      'The Landed Cost Return Variance account is inactive, a group account, or belongs to another company. A Finance administrator must correct it under Company Profile → Sales & inventory GL defaults.',
  },
  PURCHASE_RETURN_EXCEEDS_RECEIPT_QUANTITY: {
    title: 'Return quantity exceeds what can still be returned',
    guidance: 'Other returns against this GRN line have already been posted. Reduce the return quantity (edit is available only in Draft) or cancel this return.',
  },
  PURCHASE_RETURN_GRN_NOT_ELIGIBLE: {
    title: 'The GRN is no longer posted',
    guidance: 'The source GRN was unposted or removed, so this return can no longer proceed. Cancel this return and raise a new one against a posted GRN if needed.',
  },
  PURCHASE_RETURN_PRIOR_RETURN_UNVALUED: {
    title: 'An earlier return blocks this one',
    guidance: 'A return on the same GRN line was posted before receipt-valuation reversal was introduced, so further returns cannot be valued automatically. Contact Finance.',
  },
  PURCHASE_RETURN_ALREADY_POSTED: {
    title: 'This return is already posted',
    guidance: 'It was posted by another user or session. The page has been refreshed.',
  },
  PURCHASE_RETURN_CONCURRENT_UPDATE: {
    title: 'This return changed while you were working',
    guidance: 'Another user updated it at the same time. The page has been refreshed; review it and try again.',
  },
  JOURNAL_ACCOUNT_INVALID: {
    title: 'A GL account of the original receipt cannot be posted to',
    guidance: 'An account used when the goods were received is now inactive or a group account. Contact Finance to reactivate it.',
  },
};

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export default function PurchaseReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { isCriticalCode } = useCriticalItemLookup();

  const { data: pr, isLoading } = useQuery({
    queryKey: ['purchase-return', id],
    queryFn: () => purchaseReturnService.findById(id!),
    enabled: !!id,
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['purchase-return', id] });
    queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
  };

  const runAction = async (key: string, fn: () => Promise<unknown>, successMessage = 'Done') => {
    setActionLoading(key);
    try {
      await fn();
      message.success(successMessage);
      refresh();
    } catch (e: any) {
      const code: string | undefined = e?.response?.data?.errors?.code || e?.response?.data?.code;
      const detail: string = e?.response?.data?.message || 'Action failed';
      const guidance = code ? RETURN_ACTION_GUIDANCE[code] : undefined;
      if (guidance) {
        if (code === 'PURCHASE_RETURN_ALREADY_POSTED' || code === 'PURCHASE_RETURN_CONCURRENT_UPDATE') refresh();
        modal.warning({
          title: guidance.title,
          content: (
            <Space direction="vertical">
              <Text>{guidance.guidance}</Text>
              <Text type="secondary">{detail}</Text>
            </Space>
          ),
        });
      } else {
        message.error(detail);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateDebitNote = async () => {
    if (!id) return;
    setActionLoading('createDN');
    try {
      const dn = await dnService.createFromPurchaseReturn(id);
      message.success('Debit note created');
      queryClient.invalidateQueries({ queryKey: ['purchase-return', id] });
      navigate(`/fin/debit-notes/${dn.id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to create debit note');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = () => {
    if (!pr) return;
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(<PrintablePurchaseReturn purchaseReturn={pr} onClose={cleanup} />);
    window.addEventListener('afterprint', cleanup);
  };

  const breadcrumbSegments = useMemo(
    () =>
      pr
        ? [
            { title: 'Supply Chain', path: '/scm/dashboard' },
            { title: 'Purchase returns', path: '/scm/procurement/purchase-returns' },
            { title: pr.return_number || 'Return' },
          ]
        : null,
    [pr?.id, pr?.return_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const prWorkflowStages = useMemo(() => {
    if (!pr) return [];
    const st = pr.status;
    const cancelled = st === 'Cancelled';
    return [
      {
        title: 'Draft',
        description: 'Entry',
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Submitted',
        description: 'Review',
        status: (cancelled ? 'wait' : st === 'Submitted' ? 'process' : ['Approved', 'Posted'].includes(st) ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Approved',
        description: 'Ready to post',
        status: (cancelled ? 'wait' : st === 'Approved' ? 'process' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Posted',
        description: 'Stock / GL',
        status: (cancelled ? 'wait' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [pr]);

  const prWorkflowCurrent = useMemo(() => {
    if (!pr || pr.status === 'Cancelled') return 0;
    const st = pr.status;
    if (st === 'Draft') return 0;
    if (st === 'Submitted') return 1;
    if (st === 'Approved') return 2;
    if (st === 'Posted') return 3;
    return 0;
  }, [pr]);

  if (!id) {
    navigate('/scm/procurement/purchase-returns');
    return null;
  }

  if (isLoading && !pr) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!pr) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">Purchase return not found</Text>
        </Card>
      </div>
    );
  }

  const isDraft = pr.status === 'Draft';
  const canEdit = isDraft;
  const canSubmit = isDraft;
  const showUniversalApproval = !!pr.approval_instance_id;
  const canApprove = pr.status === 'Submitted' && !showUniversalApproval;
  const canPost =
    (pr.status === 'Approved' || (pr.status === 'Submitted' && !pr.approval_instance_id)) &&
    !['Posted', 'Cancelled'].includes(pr.status);
  const canCancel = !['Posted', 'Cancelled'].includes(pr.status);
  const isPosted = pr.status === 'Posted';

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-returns')}>
        Back
      </Button>
      {canEdit && (
        <Button type="default" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/purchase-returns/${pr.id}/edit`)}>
          Edit
        </Button>
      )}
      <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
        Print
      </Button>
      <ReportDocumentActions
        reportKey="purchase-return"
        params={{ id: pr.id }}
        documentId={pr.id}
        sendPermissions={['procurement.grn.edit', 'procurement.po.edit']}
        emailLabel="Email supplier"
      />
      {isPosted && (
        <Button
          type="primary"
          icon={<FileTextOutlined />}
          loading={actionLoading === 'createDN'}
          onClick={handleCreateDebitNote}
        >
          Create Debit Note
        </Button>
      )}
      {canSubmit && (
        <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => purchaseReturnService.submit(id!))}>
          Submit
        </Button>
      )}
      {canApprove && (
        <Button
          type="primary"
          loading={actionLoading === 'approve'}
          icon={<CheckOutlined />}
          onClick={() => runAction('approve', () => purchaseReturnService.approve(id!, undefined))}
        >
          Approve
        </Button>
      )}
      {canPost && (
        <Popconfirm
          title="Post this purchase return?"
          description="Stock and the GRN receipt valuation will be reversed at the value originally received. Supplier credit and VAT are handled by the Debit Note."
          okText="Post"
          onConfirm={() =>
            runAction(
              'post',
              () => purchaseReturnService.post(id!),
              'Purchase return posted: stock and receipt valuation reversed. Create a Debit Note for the supplier credit.'
            )
          }
        >
          <Button type="primary" loading={actionLoading === 'post'}>
            Post
          </Button>
        </Popconfirm>
      )}
      {canCancel && (
        <Popconfirm title="Cancel this purchase return?" onConfirm={() => runAction('cancel', () => purchaseReturnService.cancel(id!))}>
          <Button danger icon={<StopOutlined />} loading={actionLoading === 'cancel'}>
            Cancel
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>Quick links</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/scm/procurement/purchase-returns')}>
          All purchase returns
        </Button>
        {pr.grn_id && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/scm/procurement/grn/${pr.grn_id}`)}>
            GRN
          </Button>
        )}
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/debit-notes')}>
          Debit notes
        </Button>
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }} className="purchase-return-detail">
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Purchase return"
            documentNumber={pr.return_number}
            statusTag={
              <Space wrap>
                <Tag color={statusColors[pr.status] ?? 'default'}>{pr.status}</Tag>
                {pr.is_posted && <Tag color="green">Posted</Tag>}
              </Space>
            }
            subtitle={<Text type="secondary">{pr.return_date ? dayjs(pr.return_date).format('DD MMM YYYY') : ''}</Text>}
            facts={
              <Space wrap>
                <Text>
                  <strong>Supplier:</strong> {pr.supplier?.supplier_name ?? pr.supplier_id ?? '—'}
                </Text>
                <Text>
                  <strong>GRN:</strong> {pr.goodsReceiptNote?.grn_number ?? pr.grn_id ?? '—'}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={prWorkflowStages} current={prWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        {showUniversalApproval && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_PURCHASE_RETURN}
              entityId={pr.id}
              onApproveViaDocument={async (remarks) => {
                await purchaseReturnService.approve(id!, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await purchaseReturnService.rejectApproval(id!, payload);
              }}
              extraInvalidateQueryKeys={[['purchase-return', id], ['purchase-returns']]}
            />
          </div>
        )}
        <Collapse
          defaultActiveKey={['hdr', 'lines']}
          items={[
            {
              key: 'hdr',
              label: 'Return details',
              children: (
                <div>
                  <Card bordered={false} styles={{ body: { padding: 0 } }}>
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                      <Descriptions.Item label="Return Date">{pr.return_date ? dayjs(pr.return_date).format('DD MMM YYYY') : '—'}</Descriptions.Item>
                      <Descriptions.Item label="Project">{headerProjectLabel(pr as any, pr.lines as any)}</Descriptions.Item>
                      <Descriptions.Item label="Department">
                        {pr.department_id
                          ? (() => {
                              const d = departments.find((x) => x.id === pr.department_id);
                              return d ? `${d.department_code} - ${d.department_name}` : pr.department_id;
                            })()
                          : '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Supplier">{pr.supplier?.supplier_name ?? pr.supplier_id ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="GRN">
                        {pr.goodsReceiptNote?.grn_number ? (
                          <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/grn/${pr.grn_id}`)}>
                            {pr.goodsReceiptNote.grn_number}
                          </Button>
                        ) : (
                          pr.grn_id ?? '—'
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Return type">{pr.return_type || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Posted date">{pr.posted_date ? dayjs(pr.posted_date).format('DD MMM YYYY HH:mm') : '—'}</Descriptions.Item>
                      {pr.return_reason && <Descriptions.Item label="Return reason">{pr.return_reason}</Descriptions.Item>}
                      {pr.remarks && <Descriptions.Item label="Remarks" span={3}>{pr.remarks}</Descriptions.Item>}
                    </Descriptions>
                  </Card>
                </div>
              ),
            },
            {
              key: 'lines',
              label: 'Return lines',
              children: (
                <div>
                  <Table<PurchaseReturnLineDTO>
                    size="small"
                    rowKey="id"
                    dataSource={pr.lines ?? []}
                    pagination={false}
                    scroll={{ x: 700 }}
                    rowClassName={(row) =>
                      getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
                    }
                    columns={[
                      { title: '#', dataIndex: 'line_number', width: 50 },
                      {
                        title: 'Item',
                        dataIndex: 'item_code',
                        width: 140,
                        render: (code: string, row) => (
                          <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
                        ),
                      },
                      {
                        title: 'Description',
                        dataIndex: 'item_description',
                        render: (desc: string, row: PurchaseReturnLineDTO) => (
                          <>
                            <div>{desc}</div>
                            <LineSpecificationsText value={row.specifications} />
                          </>
                        ),
                      },
                      {
                        title: 'Supplier Description',
                        dataIndex: 'supplier_description',
                        render: (v: string, row: PurchaseReturnLineDTO) => v || row.item_description || '—',
                      },
                      { title: 'Project', key: 'project', width: 120, render: (_: unknown, row: PurchaseReturnLineDTO) => projectLabel(row as any) },
                      { title: 'Return Qty', dataIndex: 'return_quantity', width: 100, align: 'right' as const, render: (v: number) => Number(v).toLocaleString() },
                      { title: 'UOM', dataIndex: 'uom', width: 60 },
                      { title: 'PO unit rate', dataIndex: 'unit_rate', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v, { maximumFractionDigits: 3 }) },
                      { title: 'PO amount', dataIndex: 'amount', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v, { maximumFractionDigits: 3 }) },
                      ...(isPosted
                        ? [
                            {
                              title: 'Receipt value reversed',
                              dataIndex: 'reversed_value_base',
                              width: 140,
                              align: 'right' as const,
                              render: (v: number | null | undefined) =>
                                formatMoney(v, { ...PRINT_PLAIN_AMOUNT_PROFILE, nullDisplay: '—' }),
                            },
                          ]
                        : []),
                      { title: 'Reason', dataIndex: 'return_reason', ellipsis: true },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_RETURN}
          entityId={id}
          readOnly={pr.status === 'Cancelled' || pr.status === 'Posted'}
          queryKeyPrefix={['documents', 'purchase-return']}
        />
      </WorkspaceLayout>
    </div>
  );
}
